import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Image, Lightning, CircleNotch, Eye, Camera, Warning, CheckCircle,
  VideoCamera, ShoppingCart
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const SC = v => v >= 60 ? 'var(--success)' : v >= 40 ? 'var(--warning)' : 'var(--error)';
const PIE_C = ['var(--success)', 'var(--warning)', 'var(--error)'];

export default function VisualSearchPage() {
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);
  const [triggering, setTriggering] = useState('');

  useEffect(() => { clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSel(r.data[0].id); }); }, []);
  useEffect(() => { if (!sel) return; axios.get(`${API}/visual-search/audits/${sel}`, auth()).then(r => { if (r.data.length > 0) setAudit(r.data[0]); }); }, [sel]);

  const handleAudit = async () => {
    setRunning(true);
    try { const r = await axios.post(`${API}/visual-search/audit/${sel}`, {}, auth()); setAudit(r.data); toast.success(`Visual: ${r.data.overall_visual_score}%`); }
    catch { toast.error('Failed'); } finally { setRunning(false); }
  };

  const trigger = (agent) => { setTriggering(agent); toast.success(`${agent} agent triggered`); setTimeout(() => setTriggering(''), 1200); };

  const sc = audit?.scores || {};
  const reach = audit?.reach || {};
  const lm = audit?.live_metrics || {};
  const clipPie = lm.clip_score ? [{ name: 'High (>70)', value: lm.clip_score.high_pct }, { name: 'Medium', value: lm.clip_score.medium_pct }, { name: 'Low (<40)', value: lm.clip_score.low_pct }] : [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="visual-search-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Visual Search</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Pixels as Data — Google Lens, Pinterest, Instagram visual discovery</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleAudit} disabled={running} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              {running ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Camera size={16} className="mr-2" />}Run Audit
            </Button>
          </div>
        </div>

        {audit ? (<>
          {/* Score + Reach */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="nx-metric-card rounded-lg text-center py-4">
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Visual Score</span>
              <p className="text-5xl font-black font-mono mt-1" style={{ color: SC(audit.overall_visual_score) }}>{audit.overall_visual_score}%</p>
              {audit.overall_visual_score < 40 && <p className="text-[8px] text-[var(--error)] font-mono mt-1">Digitally Illiterate to Crawlers</p>}
            </div>
            {[
              { label: 'Google Lens', value: reach.google_lens_impressions, icon: Eye },
              { label: 'Pinterest', value: reach.pinterest_discovery_volume, icon: Image },
              { label: 'Instagram', value: reach.instagram_visual_taps, icon: Camera },
            ].map(m => {
              const Icon = m.icon;
              return (
                <div key={m.label} className="nx-metric-card rounded-lg py-4">
                  <div className="flex items-center gap-1.5 mb-1"><Icon size={12} className="text-[var(--accent)]" weight="duotone" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">{m.label}</span></div>
                  <p className="text-2xl font-black text-[var(--text-primary)] font-mono">{(m.value || 0).toLocaleString()}</p>
                </div>
              );
            })}
          </div>

          {/* Sub-metrics */}
          <div className="nx-metric-card rounded-lg">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Sub-Metric Breakdown</h3>
            <div className="space-y-2">
              {[
                { key: 'alt_text_coverage', label: 'Alt Text Coverage', target: '100%', flag: sc.alt_text_coverage < 30 },
                { key: 'exif_data_present', label: 'EXIF Data Present' },
                { key: 'product_schema_images', label: 'Product Schema Images', note: 'Product/Offer JSON-LD' },
                { key: 'pinterest_rich_pins', label: 'Pinterest Rich Pins', note: 'Dynamic data sync' },
                { key: 'google_lens_eligible', label: 'Google Lens Eligible', note: '720p+ resolution' },
              ].map(m => (
                <div key={m.key} className="flex items-center gap-3">
                  <span className="text-[10px] text-[var(--text-secondary)] w-36">{m.label}</span>
                  {m.flag && <span className="text-[8px] bg-[var(--error-bg)] text-[var(--error)] px-1.5 py-0.5 rounded font-mono">Red Flag</span>}
                  <div className="flex-1 h-3 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${sc[m.key] || 0}%`, background: SC(sc[m.key] || 0) }} />
                  </div>
                  <span className="text-sm font-bold font-mono text-[var(--text-primary)] w-12 text-right">{sc[m.key] || 0}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Intelligence */}
          {audit.intelligence?.length > 0 && (
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3"><Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Actions</h3>
              <table className="w-full border-collapse">
                <thead><tr className="border-b border-[var(--border)]"><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th></tr></thead>
                <tbody>
                  {audit.intelligence.map(r => (
                    <tr key={r.id} className="border-b border-[var(--border-light)]">
                      <td className="p-2"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: r.severity === 'high' ? 'var(--error)' : 'var(--warning)' }} /><span className="text-xs text-[var(--text-primary)]">{r.indicator}</span></span></td>
                      <td className="p-2 text-xs text-[var(--text-secondary)]">{r.intelligence}</td>
                      <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1">{r.action}</p>
                        <Button size="sm" onClick={() => trigger(r.agent)} disabled={triggering === r.agent} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3"><Lightning size={10} className="mr-1" />Execute</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 4 Live Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* M1: CLIP Score */}
            {lm.clip_score && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Asset AI Readability (CLIP Score)</span>
                <div className="flex items-center gap-4">
                  <div className="h-36 w-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart><Pie data={clipPie} dataKey="value" cx="50%" cy="50%" outerRadius={55} innerRadius={30}>{clipPie.map((_, i) => <Cell key={i} fill={PIE_C[i]} />)}</Pie><Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 10 }} /></PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1">
                    {clipPie.map((d, i) => <div key={d.name} className="flex items-center gap-2 text-[10px]"><span className="w-2 h-2 rounded-full" style={{ background: PIE_C[i] }} /><span className="text-[var(--text-secondary)]">{d.name}</span><span className="font-mono text-[var(--text-primary)] ml-auto">{d.value}%</span></div>)}
                  </div>
                </div>
                {lm.clip_score.flagged_assets?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-[9px] text-[var(--error)] font-mono">Flagged Assets:</p>
                    {lm.clip_score.flagged_assets.map(a => (
                      <div key={a.asset} className="flex items-center gap-2 p-1.5 rounded bg-[var(--error-bg)] text-[9px] font-mono">
                        <span className="text-[var(--text-primary)]">{a.asset}</span>
                        <span className="text-[var(--text-tertiary)]">Tagged: "{a.tag}"</span>
                        <span className="text-[var(--error)] ml-auto">AI reads: "{a.ai_reads_as}"</span>
                        <span className="text-[var(--warning)]">{a.clip_score}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* M2: Visual Sentiment */}
            {lm.visual_sentiment && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Visual Sentiment Polarity</span>
                <p className="text-3xl font-black font-mono mb-3" style={{ color: SC(lm.visual_sentiment.score) }}>{lm.visual_sentiment.score}/100</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {lm.visual_sentiment.positive?.map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--success-bg)] text-[var(--success)] rounded font-mono">{p}</span>)}
                </div>
                <div className="flex flex-wrap gap-1">
                  {lm.visual_sentiment.negative?.map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--error-bg)] text-[var(--error)] rounded font-mono">{p}</span>)}
                </div>
                {lm.visual_sentiment.score < 60 && <p className="text-[8px] text-[var(--warning)] font-mono mt-2">Creative Lab briefed to refresh lowest-performing visual assets</p>}
              </div>
            )}

            {/* M3: Video OCR */}
            {lm.video_ocr && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block"><VideoCamera size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Video OCR Extraction Rate</span>
                <div className="space-y-2">
                  {Object.entries(lm.video_ocr.platforms || {}).map(([p, v]) => (
                    <div key={p} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-24 capitalize">{p.replace('_', ' ')}</span>
                      <div className="flex-1 h-2.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${v}%`, background: SC(v) }} />
                      </div>
                      <span className="text-[10px] font-mono text-[var(--text-primary)] w-10 text-right">{v}%</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 mt-2 p-1.5 rounded border border-[var(--border-light)]">
                    <span className="text-[10px] text-[var(--text-secondary)]">Arabic On-Screen OCR</span>
                    <span className={`text-sm font-bold font-mono ${lm.video_ocr.arabic_ocr >= 50 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{lm.video_ocr.arabic_ocr}%</span>
                    {lm.video_ocr.arabic_alert && <Warning size={10} className="text-[var(--error)] ml-auto" weight="fill" />}
                  </div>
                </div>
                {lm.video_ocr.arabic_alert && <p className="text-[8px] text-[var(--error)] font-mono mt-1">Agent flags: caption redesign needed for higher contrast Arabic typeface</p>}
              </div>
            )}

            {/* M4: Buy-Box Overlap */}
            {lm.buybox_overlap && (
              <div className="nx-metric-card rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]"><ShoppingCart size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Buy-Box Overlap (Visual)</span>
                  {lm.buybox_overlap.alert && <span className="text-[9px] bg-[var(--error-bg)] text-[var(--error)] px-2 py-0.5 rounded font-mono">Alert: 3P winning</span>}
                </div>
                <div className="space-y-2">
                  {Object.entries(lm.buybox_overlap.marketplaces || {}).map(([mp, d]) => (
                    <div key={mp} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-16 capitalize">{mp}</span>
                      <div className="flex-1 h-3 bg-[var(--neutral-bg)] rounded-full overflow-hidden flex">
                        <div className="h-full bg-[var(--accent)]" style={{ width: `${d.brand_captures}%` }} title={`Brand: ${d.brand_captures}%`} />
                        <div className="h-full bg-[var(--error)]" style={{ width: `${d.third_party}%` }} title={`3P: ${d.third_party}%`} />
                      </div>
                      <span className="text-[9px] font-mono text-[var(--accent)] w-10">{d.brand_captures}%</span>
                      <span className="text-[9px] font-mono text-[var(--error)] w-10">{d.third_party}%</span>
                    </div>
                  ))}
                </div>
                {lm.buybox_overlap.alert && <p className="text-[8px] text-[var(--error)] font-mono mt-2">Marketplace Agent triggered to correct product image ownership signals</p>}
              </div>
            )}
          </div>
        </>) : (
          <div className="nx-metric-card rounded-lg text-center py-16">
            <Camera size={48} className="mx-auto mb-4 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Click "Run Audit" to analyze visual search readiness</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
